import { zodResolver } from "@hookform/resolvers/zod"
import {
  createTeamSpace,
  TeamSpace,
  type TeamSpaceUpsert,
  updateRoles,
  updateTeamSpace,
  type UpdateTeamSpaceRoles,
  useInvalidateRoles,
  useInvalidateTeamSpaces,
} from "@prismicio/editor-fields"
import {
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  Form,
  ScrollArea,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation } from "@tanstack/react-query"
import { useId, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import * as z from "zod"

import { env } from "../../app/env"
import { getRepositoryName } from "../../app/routing"
import { useTeamSpaces } from "../../app/services/role"
import {
  trackTeamSpaceCreated,
  trackTeamSpaceUpdated,
} from "../../app/services/tracking"
import { prepareUpdateRolesBody } from "./CustomRoles/CustomRoles.utils"
import { getTeamSpaceLabel, useTeamSpaceNavigation } from "./TeamSpaces.utils"
import { TeamSpacesCustomRolesTab } from "./TeamSpacesCustomRolesTab"
import { TeamSpacesDialogGeneralTab } from "./TeamSpacesDialogGeneralTab"
import { HeaderTabs } from "./TeamSpacesDialogTabs"
import { useTeamSpaceSettingsDeepLink } from "./useTeamSpacesSettingsDeepLink"

interface TeamSpacesDialogProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  teamSpace?: TeamSpace
}

export function TeamSpacesDialog(props: TeamSpacesDialogProps) {
  const { isOpen, setIsOpen, teamSpace } = props
  const deepLink = useTeamSpaceSettingsDeepLink()

  function closeSettings() {
    setIsOpen(false)
    deepLink.cleanupSearchParams()
  }

  const { mutate: createTeamSpace, isPending: isCreating } =
    useCreateTeamSpaceMutation({
      onSuccess: closeSettings,
    })

  const { mutate: updateTeamSpace, isPending: isUpdating } =
    useUpdateTeamSpaceMutation({
      onSuccess: closeSettings,
    })

  const isLoading = isCreating || isUpdating

  return (
    <Dialog
      onOpenChange={(open) => {
        if (isLoading) return
        if (!open) deepLink.cleanupSearchParams()
        setIsOpen(open)
      }}
      open={isOpen}
      closeButtonDisabled={isLoading}
      noDescription
    >
      <TeamSpacesDialogContent
        closeSettings={closeSettings}
        teamSpace={teamSpace}
        isLoading={isLoading}
        createTeamSpace={createTeamSpace}
        updateTeamSpace={updateTeamSpace}
      />
    </Dialog>
  )
}

interface UseTeamSpaceFormSchemaArgs {
  teamSpace?: TeamSpace
}

function useTeamSpaceFormSchema(args: UseTeamSpaceFormSchemaArgs) {
  const { teamSpace } = args

  const teamSpaces = useTeamSpaces()

  return useMemo(() => {
    const teamSpaceNames = teamSpaces.map((teamSpace) => teamSpace.name)

    return z.object({
      name: z
        .string()
        .trim()
        .min(1, "This field is required")
        .max(50)
        .refine(
          // Disallow duplicate names, but allow the same name when editing
          (name) => !teamSpaceNames.includes(name) || teamSpace?.name === name,
          {
            message: "A Space with this name already exists",
          },
        ),
      customTypes: z.array(z.string()),
    })
  }, [teamSpace, teamSpaces])
}

export type TeamSpaceFormSchema = z.infer<
  ReturnType<typeof useTeamSpaceFormSchema>
>

interface TeamSpacesDialogContentProps {
  teamSpace?: TeamSpace
  isLoading?: boolean
  closeSettings: () => void
  createTeamSpace: (vars: CreateTeamSpaceMutationVariables) => void
  updateTeamSpace: (vars: UpdateTeamSpaceMutationVariables) => void
}

function TeamSpacesDialogContent(props: TeamSpacesDialogContentProps) {
  const {
    teamSpace,
    isLoading,
    closeSettings,
    createTeamSpace,
    updateTeamSpace,
  } = props
  const deepLink = useTeamSpaceSettingsDeepLink()
  const [tab, setTab] = useState(deepLink.tab)

  const TeamSpaceFormSchema = useTeamSpaceFormSchema({ teamSpace })

  // general tab state
  const form = useForm<TeamSpaceFormSchema>({
    resolver: zodResolver(TeamSpaceFormSchema),
    defaultValues: {
      name: teamSpace?.name ?? "",
      customTypes: teamSpace?.customTypes ?? [],
    },
  })

  const generalTabDirty = form.formState.isDirty

  // roles tab state
  const [roleUpdates, setRoleUpdates] = useState<UpdateTeamSpaceRoles>({})

  const rolesTabDirty = Boolean(
    roleUpdates && Object.keys(roleUpdates).length > 0,
  )

  // id used for linking native form and submit button
  const formId = useId()

  return (
    <>
      <DialogHeader title={getDialogTitle(teamSpace)} icon="folderManaged" />
      <DialogContent paddingInline={8} gap={0}>
        <HeaderTabs tab={tab} setTab={setTab} teamSpace={teamSpace} />
        <FormProvider {...form}>
          <Form
            id={formId}
            sx={{
              height: "100%",
              overflowY: "hidden",
              marginInline: 8,
            }}
            onSubmit={form.handleSubmit(
              (data) => {
                if (teamSpace) {
                  updateTeamSpace({
                    teamSpaceId: teamSpace.id,
                    teamSpace: data,
                    oldTeamSpace: teamSpace,
                    generalTabDirty,
                    roleUpdates,
                    rolesTabDirty,
                  })
                } else {
                  createTeamSpace({
                    teamSpace: data,
                    roleUpdates,
                    rolesTabDirty,
                  })
                }
              },
              // on error, switch to general tab
              () => setTab("general"),
            )}
          >
            {tab === "general" && (
              <ScrollArea
                style={{
                  gap: 16,
                  paddingBlock: 16,
                  paddingInline: 2,
                }}
              >
                <TeamSpacesDialogGeneralTab
                  teamSpace={teamSpace}
                  closeSettings={closeSettings}
                />
              </ScrollArea>
            )}
            {tab === "roles" && (
              <TeamSpacesCustomRolesTab
                teamSpace={teamSpace}
                roleUpdates={roleUpdates}
                setRoleUpdates={setRoleUpdates}
              />
            )}
          </Form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <DialogCancelButton disabled={isLoading} />
        <DialogActionButton
          type="submit"
          form={formId}
          loading={isLoading}
          disabled={!generalTabDirty && !rolesTabDirty}
        >
          Save
        </DialogActionButton>
      </DialogActions>
    </>
  )
}

function getDialogTitle(teamSpace?: TeamSpace) {
  if (!teamSpace) return "New Space settings"
  return `${getTeamSpaceLabel(teamSpace)} settings`
}

type CreateTeamSpaceMutationVariables = {
  teamSpace: TeamSpaceUpsert
  roleUpdates: UpdateTeamSpaceRoles
  rolesTabDirty: boolean
}

interface UseCreateTeamSpaceMutationArgs {
  onSuccess?: () => void
}

function useCreateTeamSpaceMutation(args: UseCreateTeamSpaceMutationArgs) {
  const { onSuccess } = args

  const { showToast } = useToast()

  const invalidateTeamSpaces = useInvalidateTeamSpaces()
  const invalidateRoles = useInvalidateRoles()

  const { navigateToTeamSpace } = useTeamSpaceNavigation()

  return useMutation({
    mutationFn: async (args: CreateTeamSpaceMutationVariables) => {
      const { teamSpace, roleUpdates, rolesTabDirty } = args

      const createdTeamSpace = await createTeamSpace({
        baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
        repository: getRepositoryName(),
        authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        payload: teamSpace,
      })

      if (rolesTabDirty) {
        await updateRoles({
          baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
          repository: getRepositoryName(),
          authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
          payload: prepareUpdateRolesBody({
            roleUpdates,
            teamSpaceId: createdTeamSpace.id,
          }),
        })
      }

      return { createdTeamSpace, hasUpdatedRoles: rolesTabDirty }
    },
    onError: () => {
      showToast({
        title: "Failed to create a Space",
        icon: "block",
      })
    },
    onSuccess: async ({ createdTeamSpace, hasUpdatedRoles }) => {
      await invalidateTeamSpaces()

      if (hasUpdatedRoles) {
        void invalidateRoles()
      }

      navigateToTeamSpace(createdTeamSpace.id, createdTeamSpace.customTypes)

      showToast({
        title: "Space created successfully",
      })

      onSuccess?.()

      trackTeamSpaceCreated(createdTeamSpace)
    },
  })
}

type UpdateTeamSpaceMutationVariables = {
  teamSpace: TeamSpaceUpsert
  teamSpaceId: string
  // for tracking purposes
  oldTeamSpace: TeamSpace
  generalTabDirty: boolean
  rolesTabDirty: boolean
  roleUpdates: UpdateTeamSpaceRoles
}

interface UseUpdateTeamSpaceMutationArgs {
  onSuccess?: () => void
}

function useUpdateTeamSpaceMutation(args: UseUpdateTeamSpaceMutationArgs) {
  const { onSuccess } = args

  const { showToast } = useToast()

  const invalidateTeamSpaces = useInvalidateTeamSpaces()
  const invalidateRoles = useInvalidateRoles()

  const { navigateToTeamSpace } = useTeamSpaceNavigation()

  return useMutation({
    mutationFn: async (vars: UpdateTeamSpaceMutationVariables) => {
      const {
        teamSpace,
        teamSpaceId,
        generalTabDirty,
        roleUpdates,
        rolesTabDirty,
      } = vars

      let updatedTeamSpace: TeamSpace | undefined

      if (generalTabDirty) {
        updatedTeamSpace = await updateTeamSpace({
          baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
          repository: getRepositoryName(),
          authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
          payload: teamSpace,
          teamSpaceId: teamSpaceId,
        })
      }

      if (rolesTabDirty) {
        await updateRoles({
          baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
          repository: getRepositoryName(),
          authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
          payload: prepareUpdateRolesBody({
            roleUpdates,
            teamSpaceId,
          }),
        })
      }

      return {
        updatedTeamSpace,
        hasUpdatedGeneral: generalTabDirty,
        hasUpdatedRoles: rolesTabDirty,
      }
    },
    onError: () => {
      showToast({
        title: "Failed to update a Space",
        icon: "block",
      })
    },
    onSuccess: async (
      { updatedTeamSpace, hasUpdatedGeneral, hasUpdatedRoles },
      variables,
    ) => {
      if (hasUpdatedGeneral) {
        await invalidateTeamSpaces()
      }

      if (hasUpdatedRoles) {
        void invalidateRoles()
      }

      navigateToTeamSpace(
        variables.teamSpaceId,
        variables.teamSpace.customTypes,
      )

      showToast({
        title: "Space updated successfully",
      })

      onSuccess?.()

      if (hasUpdatedGeneral && updatedTeamSpace) {
        trackTeamSpaceUpdated({
          old: variables.oldTeamSpace,
          new: updatedTeamSpace,
        })
      }
    },
  })
}
